// ============================================================================
// FSA CORPORATE FACTS HOOK (ADR-0024 §2)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { fsaCorporateFactsService } from 'api/services/fsa/fsaCorporateFactsService';
import type { ID } from 'api/types';
import type { CorporateFacts, CorporateFactsData } from 'api/types/fsa/corporateFacts.types';

export const useFsaCorporateFacts = (engagementId: ID | null | undefined) => {
  const [data, setData] = useState<CorporateFacts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaCorporateFactsService.get(engagementId);
      setData(r.data ?? null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
};

/** Parse the raw `data` blob; a malformed blob yields {} rather than throwing. */
export function parseCorporateFactsData(raw: string | null | undefined): CorporateFactsData {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}
